import React, { useEffect, useState } from 'react';

const EMPTY = {
  name: '',
  requiresMatter: false,
  isActive: true,
  sortOrder: 0,
};

export default function AssignmentTypeFormModal({ open, assignmentType, existingTypes, onClose, onSave }) {
  const [values, setValues] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setValues({
      ...EMPTY,
      ...(assignmentType || {}),
      name: assignmentType?.name || '',
      requiresMatter: Boolean(assignmentType?.requiresMatter),
      isActive: assignmentType?.isActive !== false,
      sortOrder: Number(assignmentType?.sortOrder) || 0,
    });
    setErrors({});
  }, [open, assignmentType]);

  if (!open) return null;

  const setField = (field, value) => {
    setValues((current) => ({ ...current, [field]: value }));
    setErrors((current) => ({ ...current, [field]: '' }));
  };

  const submit = async (event) => {
    event.preventDefault();
    const nextErrors = {};
    const name = values.name.trim();
    if (!name) nextErrors.name = 'Assignment Type is required.';
    const duplicate = existingTypes.some(
      (item) => item.id !== assignmentType?.id && String(item.name || '').trim().toLowerCase() === name.toLowerCase()
    );
    if (duplicate) nextErrors.name = 'An assignment type with this name already exists.';
    if (Number(values.sortOrder) < 0) nextErrors.sortOrder = 'Display order cannot be negative.';
    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      return;
    }

    setSaving(true);
    try {
      await onSave({ ...values, name });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center p-4">
      <form onSubmit={submit} className="bg-white rounded-lg shadow-xl w-full max-w-lg p-6">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h2 className="text-xl font-bold">{assignmentType ? 'Edit Assignment Type' : 'Add Assignment Type'}</h2>
            <p className="text-sm text-gray-500">Control assignment availability and Matter requirement.</p>
          </div>
          <button type="button" onClick={onClose} className="text-2xl leading-none text-gray-500">×</button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Assignment Type *</label>
            <input
              value={values.name}
              onChange={(event) => setField('name', event.target.value)}
              className={`w-full px-3 py-2 border rounded ${errors.name ? 'border-red-500' : ''}`}
              placeholder="e.g. GST Notice"
              autoFocus
            />
            {errors.name && <p className="text-red-600 text-xs mt-1">{errors.name}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Display Order</label>
            <input
              type="number"
              min="0"
              value={values.sortOrder}
              onChange={(event) => setField('sortOrder', event.target.value)}
              className={`w-full px-3 py-2 border rounded ${errors.sortOrder ? 'border-red-500' : ''}`}
            />
            {errors.sortOrder && <p className="text-red-600 text-xs mt-1">{errors.sortOrder}</p>}
          </div>

          <label className="flex items-start gap-3 p-3 border rounded cursor-pointer">
            <input
              type="checkbox"
              checked={values.requiresMatter}
              onChange={(event) => setField('requiresMatter', event.target.checked)}
              className="mt-1"
            />
            <span>
              <span className="block font-medium">Requires Matter</span>
              <span className="block text-sm text-gray-500">Assignments of this type must later be linked to a Matter / Case Diary record.</span>
            </span>
          </label>

          <label className="flex items-start gap-3 p-3 border rounded cursor-pointer">
            <input
              type="checkbox"
              checked={values.isActive}
              onChange={(event) => setField('isActive', event.target.checked)}
              className="mt-1"
            />
            <span>
              <span className="block font-medium">Active</span>
              <span className="block text-sm text-gray-500">Only active assignment types appear while creating new assignments.</span>
            </span>
          </label>
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <button type="button" onClick={onClose} disabled={saving} className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300">Cancel</button>
          <button type="submit" disabled={saving} className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-60">
            {saving ? 'Saving...' : 'Save Assignment Type'}
          </button>
        </div>
      </form>
    </div>
  );
}
